import type { IconName } from "@/components/icons/Icon";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Hotkey } from "@/utils/hotkeys";

/**
 * The list of inline text styles (bold, italic, …), shared by the sidebar dropdown and
 * the right-click menu so both stay in sync. Each one knows its icon, its
 * active state, disabled state, and how to toggle it.
 *
 * Their labels are in en.json under `suggestions.inlineTextStyle`.
 */

export type InlineTextStyleKey =
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | "clear"
  | "inlineCode";

export type InlineTextStyle = {
  key: InlineTextStyleKey;
  icon: IconName;
  hotkey?: Hotkey;
  isActive: (doc: DocumentModel) => boolean;
  isDisabled: (doc: DocumentModel) => boolean;
  apply: (doc: DocumentModel) => void;
};

function disabledInExpressionOrCode(doc: DocumentModel): boolean {
  return !!(doc.editor.editorState.isExpression || doc.editor.editorState.isCode);
}

export const INLINE_TEXT_STYLES: InlineTextStyle[] = [
  {
    key: "bold",
    icon: "text-style/Bold",
    hotkey: "bold",
    isActive: (doc) => !!doc.editor.editorState.isBold,
    isDisabled: disabledInExpressionOrCode,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().toggleBold().run();
    },
  },
  {
    key: "italic",
    icon: "text-style/Italic",
    hotkey: "italic",
    isActive: (doc) => !!doc.editor.editorState.isItalic,
    isDisabled: disabledInExpressionOrCode,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().toggleItalic().run();
    },
  },
  {
    key: "underline",
    icon: "text-style/Underline",
    hotkey: "underline",
    isActive: (doc) => !!doc.editor.editorState.isUnderline,
    isDisabled: disabledInExpressionOrCode,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().toggleUnderline().run();
    },
  },
  {
    key: "strikethrough",
    icon: "text-style/Strike",
    hotkey: "strikethrough",
    isActive: (doc) => !!doc.editor.editorState.isStrikethrough,
    isDisabled: disabledInExpressionOrCode,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().toggleStrike().run();
    },
  },
  {
    key: "inlineCode",
    icon: "text-style/InlineCode",
    hotkey: "inlineCode",
    isActive: (doc) => !!doc.editor.editorState.isCode,
    isDisabled: (doc) => !!doc.editor.editorState.isExpression,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().toggleCode().run();
    },
  },
  {
    key: "clear",
    icon: "text-style/Clear",
    isActive: () => false,
    isDisabled: (doc) => !!doc.editor.editorState.isExpression,
    apply: (doc) => {
      doc.editor.tiptap?.chain().focus().clearFormatting().run();
    },
  },
];
